import { axiosInstance, axiosInstanceV2 } from '../axios.ts';
import { IUser, IUserRegistrationData } from '../types';
import { cleanCPFCNPJ, snakeCaseDictToCamelCaseDict } from '../utils.ts';

export interface GetTokenResponse {
  token: string;
  tokenExpirationDate?: string;
  agreement_id?: number;
  user: IUser;
}

export async function getToken({
  username,
  password,
  agreement_id,
  integratedLogin,
  idp,
}: {
  username: string;
  password?: string;
  integratedLogin?: string;
  idp?: string;
  agreement_id?: number | string;
}): Promise<GetTokenResponse> {
  if (integratedLogin === 'voalle') {
    return axiosInstance.post('/voalle/login/', { username, password, idp }).then(({ data: { token, ...data } }) => ({
      token: token,
      tokenExpirationDate: data.token_expiration_date,
      user: snakeCaseDictToCamelCaseDict(data) as IUser,
      agreement_id: data.agreement_id,
    }));
  }
  if (integratedLogin === 'surf') {
    return axiosInstance.post('/surf/login/', { document: username }).then(({ data: { token, ...data } }) => ({
      token: token,
      tokenExpirationDate: data.token_expiration_date,
      user: snakeCaseDictToCamelCaseDict(data) as IUser,
      agreement_id: data.agreement_id,
    }));
  }

  if (integratedLogin === 'mk') {
    return axiosInstance.post('/mk/login/', { username, password, agreement_id }).then(({ data: { token, ...data } }) => {
      return {
        token: token,
        tokenExpirationDate: data.token_expiration_date,
        user: snakeCaseDictToCamelCaseDict(data) as IUser,
        agreement_id: data.agreement_id,
      };
    });
  }

  if (integratedLogin === 'adapter') {
    return axiosInstance.post('/adapter/login/', { username, password }).then(({ data: { token, ...data } }) => ({
      token: token,
      tokenExpirationDate: data.token_expiration_date,
      user: snakeCaseDictToCamelCaseDict(data) as IUser,
      agreement_id: data.agreement_id,
    }));
  }
  return axiosInstance
    .post('/users/tokens/' + ((agreement_id && '?agreement_id=' + agreement_id) || ''), {
      username,
      password,
      agreement_id,
    })
    .then(({ data }) => ({
      token: data.token,
      user: snakeCaseDictToCamelCaseDict(data.user) as IUser,
      agreement_id: data.agreement_id,
    }));
}

export function registerUser(user: IUserRegistrationData, agreementId: number): Promise<IUser & { token: string }> {
  return axiosInstance
    .post(
      `/profile/?agreement_id=${agreementId}`,
      { ...user, cpf: cleanCPFCNPJ(user.cpf) },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Agreement-Id': agreementId,
        },
      }
    )
    .then(({ data }) => {
      localStorage.setItem('agreementId', agreementId.toString());

      return snakeCaseDictToCamelCaseDict(data) as IUser & { token: string };
    });
}

export async function getExClientLogin({ cpf }: { cpf: string }): Promise<GetTokenResponse> {
  return axiosInstanceV2.post('/agreement/ex_client/', { cpf }).then(({ data }) => ({
    token: data.token,
    user: snakeCaseDictToCamelCaseDict(data.user) as IUser,
  }));
}

export async function validateCPF(cnpjCpf: string, agreementId: number) {
  return axiosInstanceV2
    .post(`/agreement/validate/?agreement_id=${agreementId}`, {
      cpf: cleanCPFCNPJ(cnpjCpf),
    })
    .then(({ data }) => data);
}

export async function vinculateCPF(cnpjCpf: string, agreementId: number) {
  return axiosInstanceV2
    .post(`/agreement/vinculate/?agreement_id=${agreementId}`, {
      cpf: cleanCPFCNPJ(cnpjCpf),
    })
    .then(({ data }) => data);
}

export function validatePromocode(code: string) {
  return axiosInstance.post('/validate_promocode/', { promocode: code });
}

export function vinculateExternalCode(code: string, agreementId: number) {
  const cleanedCode = code.replace(/[^a-zA-Z0-9]/g, '');
  return axiosInstanceV2
    .post(`/agreement/vinculate/?agreement_id=${agreementId}`, {
      external_code: cleanedCode,
    })
    .then(({ data }) => data);
}
